// utils/buildNominationExportData.js

import countryList from 'react-select-country-list';
import { strapiUrl } from './constants';
import { getSectionalCommitteeHistoryString } from './getSectionalCommitteeHistoryString';
import { getNominationRoundYear } from './getNominationRoundYear';

export const buildNominationExportData = (
  nomination,
  {
    nominationStatusTypes,
    electionTypes,
    organisations,
    titlesTypes,
    stateTerritories,
    genderTypes,
    australianStatus,
    sectionalCommitteesWithCmse,
    sectionalCommitteeHistoryArray,
    candidateArray,
    candidateStateArray,
    proposerSeconderArray,
    nominationStateArray,
    nominationSupporterArray,
    nominationDeclarationArray,
    nominationRefereeArray,
    fellowsArray,
    nonAasRefereesArray,
  },
) => {
  const attrs = nomination.attributes;
  const nominationId = nomination.id;

  const candidate =
    candidateArray.find((c) => c.id === attrs.candidateId)?.attributes || {};

  const candidateState = candidateStateArray.find(
    (s) => s.attributes.candidateId === attrs.candidateId,
  )?.attributes;

  const nominationState = nominationStateArray.find(
    (s) => s.attributes.nominationId === nominationId,
  )?.attributes;

  const proposerSeconder = proposerSeconderArray.find(
    (p) => p.attributes.nominationId === nominationId,
  )?.attributes;

  const nominationSupporter = nominationSupporterArray.find(
    (s) => s.attributes.nominationId === nominationId,
  )?.attributes;

  const nominationDeclaration = nominationDeclarationArray.find(
    (d) => d.attributes.nominationId === nominationId,
  )?.attributes;

  const nominationReferee = nominationRefereeArray.find(
    (r) => r.attributes.nominationId === nominationId,
  )?.attributes;

  const getFellowLabel = (id) => {
    if (!id) return null;
    const fellow = fellowsArray.find((f) => f.id === id);
    return fellow?.attributes?.label ?? null;
  };

  const getNonAasLabel = (id) => {
    if (!id) return null;
    const ref = nonAasRefereesArray.find((r) => r.id === id)?.attributes;
    return ref?.label || (ref ? `${ref.name} ${ref.lastName} (${ref.email})` : null);
  };

  const getLabelFromJson = (jsonData) => {
    if (jsonData !== null && jsonData !== undefined) {
      if (Array.isArray(jsonData)) {
        return jsonData.map((item) => item.label).join(', ');
      }
    }
    return null;
  };

  const getSupporterType = (id) => {
    if (!id) return '';
    return id === 1 ? 'Personal' : 'General';
  };

  const NOT_LISTED_LABEL = '-- my organisation is not listed --';
  let organisationName = organisations[candidate?.organisationId];
  if (
    organisationName?.toLowerCase() === NOT_LISTED_LABEL.toLowerCase() &&
    typeof candidate?.otherOrganisation === 'string'
  ) {
    organisationName = candidate.otherOrganisation;
  }

  return {
    id: nomination.id,
    createdAt: attrs.createdAt,
    updatedAt: attrs.updatedAt,
    nominationRoundYear:
      typeof nominationState?.roundYear === 'number'
        ? nominationState.roundYear
        : null,
    nominationStatus: nominationStatusTypes[attrs.nominationStatusTypeId],
    electionType: electionTypes[attrs.electionTypeId],
    ranking:
      typeof nominationState?.ranking === 'number'
        ? nominationState.ranking
        : null,
    consideredYears:
      typeof candidateState?.consideredYears === 'number'
        ? candidateState.consideredYears
        : null,
    lastConsideredDate:
      typeof nominationState?.lastConsideredDate === 'string'
        ? getNominationRoundYear(new Date(nominationState.lastConsideredDate))
        : null,
    title: titlesTypes[candidate?.titleId],
    name: typeof candidate?.name === 'string' ? candidate.name : null,
    lastName:
      typeof candidate?.lastName === 'string' ? candidate.lastName : null,
    phone: typeof candidate?.phone === 'string' ? candidate.phone : null,
    address: typeof candidate?.address === 'string' ? candidate.address : null,
    email: typeof candidate?.email === 'string' ? candidate.email : null,
    stateTerritory: stateTerritories[candidate?.stateTerritoryId],
    gender: genderTypes[candidate?.genderId],
    otherGender:
      typeof candidate?.otherGender === 'string' ? candidate.otherGender : null,
    dob: typeof candidate?.dob === 'string' ? candidate.dob : null,
    countryOfBirth: candidate?.countryOfBirthId
      ? countryList().getLabel(candidate?.countryOfBirthId)
      : '',
    australianStatus: australianStatus[candidate?.australianStatusId],
    postNominal:
      typeof candidate?.postNominal === 'string' ? candidate.postNominal : null,
    jobTitle:
      typeof candidate?.jobTitle === 'string' ? candidate.jobTitle : null,
    organisation: organisationName,
    otherOrganisation:
      typeof candidate?.otherOrganisation === 'string'
        ? candidate.otherOrganisation
        : null,
    phdAwardedYear:
      typeof candidate?.phdAwardedYear === 'number'
        ? candidate.phdAwardedYear
        : null,
    noPhd: !candidate?.phdAwardedYear,
    personalInformation:
      typeof candidate?.personalInformation === 'string'
        ? candidate.personalInformation
        : null,
    diverseBackground:
      typeof candidate?.diverseBackground === 'boolean' &&
      candidate?.diverseBackground
        ? 1
        : 0,
    nominationConsentFilePath: nominationDeclaration?.nominationConsent?.data
      ?.attributes?.url
      ? `${strapiUrl}${nominationDeclaration.nominationConsent.data.attributes.url}`
      : '',
    retractedPapers: !!candidateState?.retractedPapers,
    correctedPapers: !!candidateState?.correctedPapers,
    publicationConcerns: !!candidateState?.publicationConcerns,
    disclosureDeclarationFilePath: nominationDeclaration?.disclosureDeclaration
      ?.data?.attributes?.url
      ? `${strapiUrl}${nominationDeclaration.disclosureDeclaration.data.attributes.url}`
      : '',
    nationalityId: getLabelFromJson(candidate?.nationalityId),
    emailEducationalInstitution: '',
    arrivalDate:
      typeof candidate?.arrivalDate === 'string' ? candidate.arrivalDate : null,
    careerInterruption: !!candidateState?.careerInterruption,
    scientificExcellence:
      typeof nominationState?.scientificExcellence === 'string'
        ? nominationState.scientificExcellence
        : null,
    citation:
      typeof nominationState?.citation === 'string'
        ? nominationState.citation
        : '',
    sectionalCommittee: sectionalCommitteesWithCmse[attrs.sectionalCommitteeId],
    sectionalCommitteeHistory: getSectionalCommitteeHistoryString(
      nomination,
      sectionalCommitteeHistoryArray,
    ),
    deprecatedSectionalCommitteeLabel:
      attrs.deprecatedSectionalCommitteeLabel ?? null,
    disciplineDescriptor: getLabelFromJson(attrs.disciplineDescriptor),
    overlapCommittee: !!attrs.overlapCommittee,
    overlapSectionalCommitteeId:
      sectionalCommitteesWithCmse[attrs.overlapSectionalCommitteeId],
    overlapDisciplineDescriptor: getLabelFromJson(
      attrs.overlapDisciplineDescriptor,
    ),
    interdisciplinaryCommittee: getLabelFromJson(
      attrs.interdisciplinaryCommittee,
    ),
    proposerFellow: getFellowLabel(proposerSeconder?.proposerFellowId),
    proposerSignatureFilePath: proposerSeconder?.proposerSignature?.data
      ?.attributes?.url
      ? `${strapiUrl}${proposerSeconder.proposerSignature.data.attributes.url}`
      : '',
    seconderFellow: getFellowLabel(proposerSeconder?.seconderFellowId),
    seconderSignatureFilePath: proposerSeconder?.seconderSignature?.data
      ?.attributes?.url
      ? `${strapiUrl}${proposerSeconder.seconderSignature.data.attributes.url}`
      : '',
    supporterOneTypeId: getSupporterType(
      nominationSupporter?.supporterOneTypeId,
    ),
    supporterOneFellow: getFellowLabel(
      nominationSupporter?.supporterOneFellowId,
    ),
    supporterOneSignatureFilePath: nominationSupporter?.supporterOneSignature
      ?.data?.attributes?.url
      ? `${strapiUrl}${nominationSupporter.supporterOneSignature.data.attributes.url}`
      : '',
    supporterTwoTypeId: getSupporterType(
      nominationSupporter?.supporterTwoTypeId,
    ),
    supporterTwoFellow: getFellowLabel(
      nominationSupporter?.supporterTwoFellowId,
    ),
    supporterTwoSignatureFileName:
      nominationSupporter?.supporterTwoSignature?.data?.attributes?.name ?? '',
    supporterThreeTypeId: getSupporterType(
      nominationSupporter?.supporterThreeTypeId,
    ),
    supporterThreeFellow: getFellowLabel(
      nominationSupporter?.supporterThreeFellowId,
    ),
    supporterThreeSignatureFilePath: nominationSupporter
      ?.supporterThreeSignature?.data?.attributes?.url
      ? `${strapiUrl}${nominationSupporter.supporterThreeSignature.data.attributes.url}`
      : '',
    supporterFourTypeId: getSupporterType(
      nominationSupporter?.supporterFourTypeId,
    ),
    supporterFourFellow: getFellowLabel(
      nominationSupporter?.supporterFourFellowId,
    ),
    supporterFourSignatureFilePath: nominationSupporter?.supporterFourSignature
      ?.data?.attributes?.url
      ? `${strapiUrl}${nominationSupporter.supporterFourSignature.data.attributes.url}`
      : '',
    meetsConduct: !!candidateState?.meetsConduct,
    extendedCitationFilePath: nominationState?.extendedCitation?.data
      ?.attributes?.url
      ? `${strapiUrl}${nominationState.extendedCitation.data.attributes.url}`
      : '',
    curriculumVitaeFilePath: nominationState?.curriculumVitae?.data?.attributes
      ?.url
      ? `${strapiUrl}${nominationState.curriculumVitae.data.attributes.url}`
      : '',
    significantPublicationsFilePath: nominationState?.significantPublications
      ?.data?.attributes?.url
      ? `${strapiUrl}${nominationState.significantPublications.data.attributes.url}`
      : '',
    publicationsFilePath: nominationState?.publications?.data?.attributes?.url
      ? `${strapiUrl}${nominationState.publications.data.attributes.url}`
      : '',
    refereeOne:
      typeof nominationReferee?.refereeOneTypeId === 'number'
        ? nominationReferee.refereeOneTypeId
        : null,
    refereeTwo:
      typeof nominationReferee?.refereeTwoTypeId === 'number'
        ? nominationReferee.refereeTwoTypeId
        : null,
    refereeThree:
      typeof nominationReferee?.refereeThreeTypeId === 'number'
        ? nominationReferee.refereeThreeTypeId
        : null,
    refereeFour:
      typeof nominationReferee?.refereeFourTypeId === 'number'
        ? nominationReferee.refereeFourTypeId
        : null,
    refereeFive:
      typeof nominationReferee?.refereeFiveTypeId === 'number'
        ? nominationReferee.refereeFiveTypeId
        : null,
    refereeSix:
      typeof nominationReferee?.refereeSixTypeId === 'number'
        ? nominationReferee.refereeSixTypeId
        : null,
    refereeSeven:
      typeof nominationReferee?.refereeSevenTypeId === 'number'
        ? nominationReferee.refereeSevenTypeId
        : null,
    refereeEight:
      typeof nominationReferee?.refereeEightTypeId === 'number'
        ? nominationReferee.refereeEightTypeId
        : null,
    refereeNine:
      typeof nominationReferee?.refereeNineTypeId === 'number'
        ? nominationReferee.refereeNineTypeId
        : null,
    refereeOneFellow: getFellowLabel(nominationReferee?.refereeOneFellowId),
    refereeTwoFellow: getFellowLabel(nominationReferee?.refereeTwoFellowId),
    refereeThreeFellow: getFellowLabel(nominationReferee?.refereeThreeFellowId),
    refereeFourFellow: getFellowLabel(nominationReferee?.refereeFourFellowId),
    refereeFiveFellow: getFellowLabel(nominationReferee?.refereeFiveFellowId),
    refereeSixFellow: getFellowLabel(nominationReferee?.refereeSixFellowId),
    refereeSevenFellow: getFellowLabel(nominationReferee?.refereeSevenFellowId),
    refereeEightFellow: getFellowLabel(nominationReferee?.refereeEightFellowId),
    refereeNineFellow: getFellowLabel(nominationReferee?.refereeNineFellowId),
    nonAasRefereeOne: getNonAasLabel(nominationReferee?.nonAasRefereeOneId),
    nonAasRefereeTwo: getNonAasLabel(nominationReferee?.nonAasRefereeTwoId),
    nonAasRefereeThree: getNonAasLabel(nominationReferee?.nonAasRefereeThreeId),
    nonAasRefereeFour: getNonAasLabel(nominationReferee?.nonAasRefereeFourId),
    nonAasRefereeFive: getNonAasLabel(nominationReferee?.nonAasRefereeFiveId),
    nonAasRefereeSix: getNonAasLabel(nominationReferee?.nonAasRefereeSixId),
    nonAasRefereeSeven: getNonAasLabel(nominationReferee?.nonAasRefereeSevenId),
    nonAasRefereeEight: getNonAasLabel(nominationReferee?.nonAasRefereeEightId),
    nonAasRefereeNine: getNonAasLabel(nominationReferee?.nonAasRefereeNineId),
    nonPreferredReferee: nominationReferee?.nonPreferredReferee ?? null,
  };
};
